import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Check, X as XIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf } from '../config/brandProfiles';
import { trackEvent } from '../utils/analytics';
import {
  ADDRESS_MAX_LENGTH,
  POSTAL_CODE_ERROR,
  POSTAL_CODE_INPUT_MAX_LENGTH,
  normalizeAddress,
  parsePostalCode,
} from '../utils/address';

interface AddressRow {
  id: string;
  address: string | null;
  postal_code: string | null;
}

/**
 * The parent's optional home address and ZIP on the Account page, viewed and edited in place
 * like the phone row above it.
 *
 * Kept separate from the Account page's own `parents` query, so the page's name and phone
 * editing does not depend on the address columns.
 *
 * Filtered by email for the same reason as the rest of the page: an admin who is also a parent
 * can read every `parents` row through RLS.
 *
 * It only *asks* on a profile whose brand has a reason to (`intake.addressPrompt`, today
 * Kaleido's resident discount), and the reason is shown with the request. An address already on
 * file shows and stays editable on either profile, since it belongs to the account.
 */
export function AccountAddressRow() {
  const { user } = useAuth();
  const { activeBrand } = useBrandProfile();
  const addressPrompt = brandProfileOf(activeBrand).intake.addressPrompt;
  const email = user?.email;
  const queryClient = useQueryClient();
  const queryKey = ['parent-address', email] as const;

  const { data: row } = useQuery<AddressRow | null>({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parents')
        .select('id, address, postal_code')
        .eq('email', email!)
        .maybeSingle();
      if (error) throw error;
      return (data as AddressRow | null) ?? null;
    },
    enabled: !!email,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [draftAddress, setDraftAddress] = useState('');
  const [draftPostalCode, setDraftPostalCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (input: { address: string | null; postal_code: string | null }) => {
      if (!row?.id) throw new Error('Parent not found');
      const { error: updateError } = await supabase.from('parents').update(input).eq('id', row.id);
      if (updateError) throw updateError;
      return input;
    },
    onSuccess: (input) => {
      setError(null);
      setIsEditing(false);
      queryClient.setQueryData(queryKey, (old: AddressRow | null | undefined) =>
        old ? { ...old, ...input } : old
      );
      // Field names only, never the values: app_events is widely readable and an address is
      // the kind of PII it should not collect. Same rule as the name and phone edits.
      trackEvent({
        eventType: 'parent_profile_update',
        user,
        userType: 'parent',
        entityType: 'parent',
        entityId: row?.id,
        metadata: { location: 'account_page', fields: Object.keys(input) },
      });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    },
  });

  if (!row) return null;

  const startEdit = () => {
    setDraftAddress(row.address ?? '');
    setDraftPostalCode(row.postal_code ?? '');
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const submit = () => {
    const postal = parsePostalCode(draftPostalCode);
    if (!postal.ok) {
      setError(POSTAL_CODE_ERROR);
      return;
    }
    save.mutate({ address: normalizeAddress(draftAddress), postal_code: postal.value });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') submit();
    if (e.key === 'Escape') cancelEdit();
  };

  const summary = [row.address, row.postal_code].filter(Boolean).join(' · ');

  // Nothing on file and no reason to ask on this profile: a BHCS parent is not asked.
  if (!summary && !isEditing && addressPrompt === null) return null;

  // Indented past the pin icon (w-4 + gap-1.5) so it lines up under the link text.
  const promptHint = addressPrompt && (
    <p className="text-xs text-gray-500 mt-0.5 ml-[1.375rem]" data-testid="account-address-prompt">
      {addressPrompt}
    </p>
  );

  return (
    <>
      {isEditing ? (
        <div className="flex items-center gap-1.5 mt-1" data-testid="account-address-edit">
          <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            value={draftAddress}
            onChange={e => setDraftAddress(e.target.value)}
            placeholder="Street address"
            aria-label="Street address"
            autoComplete="street-address"
            maxLength={ADDRESS_MAX_LENGTH}
            autoFocus
            className="min-w-0 flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyDown={onKeyDown}
          />
          <input
            type="text"
            value={draftPostalCode}
            onChange={e => setDraftPostalCode(e.target.value)}
            placeholder="ZIP"
            aria-label="ZIP code"
            autoComplete="postal-code"
            inputMode="numeric"
            maxLength={POSTAL_CODE_INPUT_MAX_LENGTH}
            className="w-20 shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyDown={onKeyDown}
          />
          <button
            onClick={submit}
            disabled={save.isPending}
            aria-label="Save address"
            className="p-1 text-green-600 hover:bg-green-50 rounded shrink-0"
          >
            <Check className="w-4 h-4" />
          </button>
          <button onClick={cancelEdit} aria-label="Cancel" className="p-1 text-gray-400 hover:bg-gray-100 rounded shrink-0">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ) : !summary ? (
        // Empty state as a visible link, not the hover pencil the rows above use: a phone has no
        // hover, and a row that says only "no address" gives no hint that it can be tapped.
        <div className="flex items-center mt-1 gap-1.5">
          <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
          <button
            onClick={startEdit}
            data-testid="account-edit-address-btn"
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Add home address (optional)
          </button>
        </div>
      ) : (
        <div className="flex items-center mt-1 gap-1.5 group">
          <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
          <p className="text-xs sm:text-sm text-gray-600 truncate">{summary}</p>
          <button
            onClick={startEdit}
            aria-label="Edit address"
            data-testid="account-edit-address-btn"
            className="p-1 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* The reason goes with the request: under the "Add" link and while editing. */}
      {(isEditing || !summary) && promptHint}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </>
  );
}
